// src/config/features.js
// ─────────────────────────────────────────────
// Central feature flags — flip here, nowhere else.
// ─────────────────────────────────────────────

export const FEATURES = {
    // UI features
    ENABLE_BUS: true,
    AGENT_TEST: false,
    ENABLE_LEAD_BOT: true,
    // "Not getting confirmed tickets?" contact form (A/B: banner above tabs vs
    // "Contact us" button in result cards).
    // Day-to-day on/off and "Call us" hours come from the backend
    // (constants.py → intent "contact_lead_config", fetched once per route search).
    // This is a hard kill switch on top: false → no config call, no banner,
    // no card button, no modal, no A/B assignment and no contact_lead API calls.
    ENABLE_CONTACT_FORM: true,

    // ── Test / environment flags ──────────────────────────────────────────
    // Bus: true  → staging ETS  (https://staging.etravelsmart.in/etsAPI/api/)
    //      false → production ETS (https://agent.etravelsmart.com/etsAPI/api/)
    BUS_TEST: false,

    // Flight: true  → TBO test environment
    //         false → TBO production
    FLIGHT_TEST: false,

    // Hotel: true  → TBO hotel test credentials + Razorpay test keys (booking, cancellation, refunds)
    //        false → production
    // Sent to the backend as hotel_test on every hotel call (booking page, cancellation drawer).
    HOTEL_TEST: true,

    // Route search WebSocket environment
    // "stage"      → wss://aluy1ihmkd.execute-api.ap-south-1.amazonaws.com/development?api_key=web_dev1
    // "production" → wss://h5nkqou4f7.execute-api.ap-south-1.amazonaws.com/production
    ROUTE_SEARCH_STAGE: "production",
    ENABLE_CURATED_TRIPS: true,
    // Curated trips section (/trips, the home page trip cards and "Plan a Trip"): when on, show only
    // the DMC trips below and hide the built-in curated trips (Manali etc.). Set false to bring them back.
    CURATED_SHOWCASE_ONLY: true,
    // path: the trip page link. seed: the built-in copy used for the card (title, photo, days, price).
    CURATED_SHOWCASE: [
        { path: "/dmc/vertical-tours-kashmir/kashmir-srinagar-gulmarg-sonamarg-pahalgm", seed: "kashmir-srinagar-gulmarg-sonamarg-pahalgm" },
        // Hornbill Festival 2026: all operators' packages on one page (src/curated/hornbill)
        { path: "/hornbill-festival", kind: "hornbill" },
    ],
    // How to Reach guides that also get the featured trip strip right under the search (holiday spots:
    // hills, beaches, backwaters). Every guide has the big banner lower down anyway; Kashmir guides always get the strip.
    // Match is on the guide's destination name, any case.
    HOLIDAY_STRIP_DESTINATIONS: [
        "Munnar", "Varkala", "Rishikesh", "Manali", "Coorg", "Goa", "Udaipur", "Pondicherry",
        "Dehradun", "Siliguri", "Kochi", "Mysore", "Jaipur",
    ],
    CURATED_TRIP_AGENT_URL: "https://721y9e5619.execute-api.ap-south-1.amazonaws.com/default/curated-trip-agent",
    // Search with fetch_avail = 0, then fetch train availability per segment in rounds.
    // false = old behaviour: backend fetches availability inside the search (fetch_avail = 1).
    AUTO_FETCH_AVAIL: true,

    // Trains checked in parallel by the automatic queue. Keep at 4 or below so manual
    // refreshes and the calendar-fares call (same API host) are not blocked.
    AVAIL_CONCURRENCY: 4,

    // ── DMC partner trips, chat and admin (partner_hub Lambda, see backend/partner_hub) ──
    ENABLE_DMC_TRIPS: true,
    // The hub runs inside the curated-trip-agent Lambda (partner_hub_helpers.py), same URL.
    // Empty = off: chat falls back to the lead bot and the partner / admin inboxes say so.
    PARTNER_HUB_URL: "https://721y9e5619.execute-api.ap-south-1.amazonaws.com/default/curated-trip-agent",
    // true + empty URL = "demo hub" kept in this browser's localStorage. Partner, admin and
    // traveller tabs in the same browser see each other's messages. For trying the flow only.
    PARTNER_HUB_LOCAL_DEMO: false,
    // Web Push public key (VAPID). The private key lives in the Lambda's environment.
    PUSH_VAPID_PUBLIC_KEY: "",
    // Google accounts that open /admin (the hub checks this list again on every call)
    ADMIN_EMAILS: ["sachin.saxena@routechef.com"],

    // DMC trip page: seconds before the "Get <partner>'s best price" contact popup opens (0 = off).
    // Not shown to partners or admin, or once the traveller is chatting. After a close it stays away this many days.
    DMC_LEAD_POPUP_SECONDS: 8,
    DMC_LEAD_POPUP_REPEAT_DAYS: 3,

    // Sponsored DMC trip ads in our own pages (src/ads/tripAds.js picks the ad; Kashmir only for now).
    // Master switch, then each slot: home (below the search), route_results (after the 3rd result),
    // how_to_reach (before the FAQ), trips (index page), curated_trip (below a curated trip).
    ENABLE_TRIP_ADS: true,
    TRIP_AD_PLACEMENTS: { home: true, route_results: true, how_to_reach: true, trips: true, curated_trip: true },
};

// Hotel calls (booking page, hotel history, cancellation) go to the orchestrator stage that
// matches hotel_test: development for test bookings, production otherwise.
const ORCHESTRATOR_BASE = "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";
export const hotelOrchestratorUrl = (hotelTest = FEATURES.HOTEL_TEST) =>
    (hotelTest ? `${ORCHESTRATOR_BASE}/development` : ORCHESTRATOR_BASE);

// Derived WebSocket URL — import this in useTravelWebSocket.js
export const ROUTE_SEARCH_WS_URL =
    FEATURES.ROUTE_SEARCH_STAGE === "production"
        ? "wss://h5nkqou4f7.execute-api.ap-south-1.amazonaws.com/production"
        : "wss://aluy1ihmkd.execute-api.ap-south-1.amazonaws.com/development?api_key=web_dev1";
